import utilService from "@/service/utilService";
import notice from "@/service/notice";
import cryptoWrapper from "@/service/cryptoWrapper";
import {mapState} from "vuex";

const SERVICE_URL=process.env.VUE_APP_SERVICE_URL;

/**
 * 방문예약 관련 Mixin
 *
 * - 승인, 반려 등 처리
 * - 알림발송 (주의: 비즈엠에 등록된 템플릿과 완전히 일치해야 함)
 *
 */
export default {
    data: () => ({
        statusInfos: {
            EDUCATION: {
                text: '교육이수전',
                color: 'black',
                backgroundColor: 'yellow',
            },
            WAITING: {
                text: '인증 대기',
                color: 'black',
                backgroundColor: 'yellow',
            },
            PENDING: {
                text: '승인 대기',
                color: 'white',
                backgroundColor: 'red',
            },
            APPROVED: {
                text: '승인',
                color: 'white',
                backgroundColor: 'blue',
            },
            REJECTED: {
                text: '반려',
                color: 'black',
                backgroundColor: 'gray',
            },
            CANCELED: {
                text: '취소',
                color: 'white',
                backgroundColor: 'black',
            }
        },
    }),

    computed: {
        ...mapState(['currentUser', 'secretKey', 'iv']),
    },

    methods: {
        getStatusInfo(statusCode) {
            return this.statusInfos[statusCode];
        },

        getEncryptedId(visitId) {
            return cryptoWrapper.encrypt(visitId, this.secretKey, this.iv);
        },

        async getApplyInfo(visitId) {
            const encryptedVisitId = this.getEncryptedId(visitId);
            const path = this.$route.path;
            const params = {};


            let url;
            let res;

            if (path.startsWith('/backoffice')) { // 개인정보 마스킹 X
                url = '/v1/res/visit/visitInfo';
                params.visitId = encryptedVisitId;
                res = await this.$axios.post(url, params);
            } else if (path.startsWith('/kiosk')) { // 개인정보 마스킹 X
                url = '/v1/kiosk/visitInfo';
                params.visitId = visitId;
                res = await this.$axios.get(url, {
                    params
                });
            } else { // 개인정보 마스킹 O
                url = '/v1/home/visit/visitInfo';
                params.visitId = encryptedVisitId;
                res = await this.$axios.post(url, params);
            }

            return {
                visitInfo: res.data.visitInfo,
                visitors: res.data.visitors,
                carryInItems: res.data.importItems
            }
        },

        /**
         * 방문 승인
         */
        async approve(visitInfo, visitor) {
            await this.$axios.post(`/v1/home/visit/approve/${visitInfo.visitId}/${visitor.visitorId}`);
            await this.notificationApproved(visitInfo, visitor);
        },

        /**
         * 방문 반려
         */
        async reject(visitInfo, visitor, reason) {
            await this.$axios.post(`/v1/home/visit/reject/${visitInfo.visitId}/${visitor.visitorId}`, {
                voidReason: reason ? reason : '(사유 없음)'
            });
            await this.notificationRejected(visitInfo, visitor, reason);
        },

        /**
         * 방문 취소
         */
        async cancel(visitInfo, visitors, reason) {
            await this.$axios.post(`/v1/home/visit/cancel/${visitInfo.visitId}`, {
                voidReason: reason
            });
            await this.notificationCanceled(visitInfo, visitors, reason);
        },

        /**
         * 방문자 도착
         */
        async arrival(visitInfo, visitor) {
            await this.$axios.post(`/v1/home/visit/arrival/${visitInfo.visitId}/${visitor.visitorId}`);
            await this.notificationArrival(visitInfo, visitor);
        },

        /**
         * 방문자 도착 알림
         */
        async notificationArrival(visitInfo, visitor) {
            const recipientPhone = visitInfo.visitUserMobile;
            const date = this.$moment(visitInfo.startDatetime).format('YYYY-MM-DD');
            const time = this.$moment(visitInfo.startDatetime).format('A hh:mm');
            const message = `[ 아이센스 방문신청 서비스 ]
방문객이 ${visitInfo.visitCompanyName} ${visitInfo.visitAreaName}에 도착했습니다.

▶ 방문자 : ${visitor.visitorName}
▶ 회사명 : ${visitor.visitorCompany}
▶ 휴대폰번호 : ${visitor.visitorMobile}
▶ 방문일시 : ${date} ${time}
▶ 방문목적 : ${visitInfo.purpose}`;
            const encryptedVisitId = this.getEncryptedId(visitInfo.visitId);
            const encryptedVisitorId = this.getEncryptedId(visitor.visitorId);
            const buttonUrl = `${SERVICE_URL}/home/visitInfo/t/${encryptedVisitId}/${encryptedVisitorId}`;
            await notice.arrivalSendSms(recipientPhone, message, buttonUrl);
        },

        async notificationRejected(visitInfo, visitor, reason) {
            const startDatetime = this.$moment(visitInfo.startDatetime);
            const startDate = startDatetime.format('YYYY-MM-DD');
            const startTime = startDatetime.format('A hh:mm');
            const message = `[ 아이센스 방문신청 서비스 ]
방문신청 반려처리 안내

▶ ${visitor.visitorName}님, ${startDate} ${startTime}
▶ 반려 사유: ${reason}

방문신청이 반려 되어 확인 후 재 신청 바랍니다.`;

            const recipientPhone = visitor.visitorMobile;
            await notice.rejectSendSms(recipientPhone, message);
        },

        async notificationApproved(visitInfo, visitor) {
            const encryptedVisitId = this.getEncryptedId(visitInfo.visitId);
            const encryptedVisitorId = this.getEncryptedId(visitor.visitorId);
            const qrcodeUrl = `${SERVICE_URL}/home/qrcode/${encryptedVisitId}/${encryptedVisitorId}`;
            const startDatetime = this.$moment(visitInfo.startDatetime);
            const startDate = startDatetime.format('YYYY-MM-DD');
            const startTime = startDatetime.format('A hh:mm');
            const message = `[ 아이센스 방문신청 서비스 ]
방문신청 승인 완료

▶ 방문회사 : ${visitInfo.visitCompanyName} ${visitInfo.visitAreaName}
▶ 방문일시 : ${startDate} ${startTime}
▶ 승인 QR 코드`;

            const recipientPhone = visitor.visitorMobile;
            await notice.confirmSendSms(recipientPhone, message, qrcodeUrl);
        },

        async notificationApplication(visitInfo, visitor) {
            const recipientPhone = cryptoWrapper.decrypt(visitInfo.visitUserEncryptedMobile, this.secretKey, this.iv);
            if (!recipientPhone) {
                this.$window.alert('접견 대상자의 휴대폰 전화번호가 없습니다.', 'error');
                return;
            }
            const encryptedVisitId = this.getEncryptedId(visitInfo.visitId);
            const encryptedVisitorId = this.getEncryptedId(visitor.visitorId);
            const date = this.$moment(visitInfo.startDatetime).format('YYYY-MM-DD');
            const time = this.$moment(visitInfo.startDatetime).format('A hh:mm');
            const approveUrl = `${SERVICE_URL}/home/visitInfo/t/${encryptedVisitId}/${encryptedVisitorId}`;
            const message = `[ 아이센스 방문신청 서비스 ]
방문신청 알림메세지

▶ 신청자 : ${visitInfo.agentName}
▶ 회사명 : ${visitInfo.agentCompany}
▶ 휴대폰번호 : ${utilService.humanReadableMobile(visitInfo.agentMobile)}
▶ 방문자 : ${visitor.visitorName}

▶ 방문회사 : ${visitInfo.visitCompanyName} ${visitInfo.visitAreaName}
▶ 방문일시 : ${date} ${time}
▶ 방문목적 : ${visitInfo.purpose}

방문 승인 완료처리 부탁드립니다.`;

            await notice.applySendSms(recipientPhone, message, approveUrl);
        },

        /**
         * 교육 이수 완료 시 승인 요청 알림 발송
         *
         * @param visitorInfo
         * @returns {Promise<void>}
         */
        async notificationApplicationEdu(visitorInfo) {
            const recipientPhone = visitorInfo?.visitUserMobile;
            if (!recipientPhone) {
                this.$window.alert('접견 대상자의 휴대폰 전화번호가 없습니다.', 'error');
                return;
            }
            const encryptedVisitId = this.getEncryptedId(visitorInfo.visitId);
            const encryptedVisitorId = this.getEncryptedId(visitorInfo.visitorId);
            const date = this.$moment(visitorInfo.startDatetime).format('YYYY-MM-DD');
            const time = this.$moment(visitorInfo.startDatetime).format('A hh:mm');
            const approveUrl = `${SERVICE_URL}/home/visitInfo/t/${encryptedVisitId}/${encryptedVisitorId}`;
            const message = `[ 아이센스 방문신청 서비스 ]
방문신청 알림메세지

▶ 신청자 : ${visitorInfo.agentName}
▶ 회사명 : ${visitorInfo.agentCompany}
▶ 휴대폰번호 : ${utilService.humanReadableMobile(visitorInfo.agentMobile)}
▶ 방문자 : ${visitorInfo.visitorName}

▶ 방문회사 : ${visitorInfo.visitCompanyName} ${visitorInfo.visitAreaName}
▶ 방문일시 : ${date} ${time}
▶ 방문목적 : ${visitorInfo.purpose}

방문 승인 완료처리 부탁드립니다.`;

            await notice.applySendSms(recipientPhone, message, approveUrl);
        },

        async notificationCanceled(visitInfo, visitors, reason) {
            const date = this.$moment(visitInfo.startDatetime).format('YYYY-MM-DD');
            const time = this.$moment(visitInfo.startDatetime).format('A hh:mm');
            const recipientPhoneList = visitors.map(e => e.visitorMobile);

            if (visitInfo.visitUserMobile) { // 접견자 휴대폰
                recipientPhoneList.push(visitInfo.visitUserMobile);
            }

            const message = `[ 아이센스 방문신청 서비스 ]
방문예약이 취소되었습니다.

▶ 신청인 : ${visitInfo.agentName}
▶ 회사명 : ${visitInfo.agentCompany}
▶ 휴대폰번호 : ${utilService.humanReadableMobile(visitInfo.agentMobile)}
▶ 방문회사 : ${visitInfo.visitCompanyName} ${visitInfo.visitAreaName}
▶ 방문일시 : ${date} ${time}
▶ 방문목적 : ${visitInfo.purpose}
▶ 취소 사유 : ${reason}`;

            for (let i in recipientPhoneList) {
                const recipientPhone = recipientPhoneList[i];
                await notice.cancelSendSms(recipientPhone, message);
            }
        },

        async notificationEducation(visitInfo, visitor) {
            const date = this.$moment(visitInfo.startDatetime).format('YYYY-MM-DD');
            const time = this.$moment(visitInfo.startDatetime).format('A hh:mm');
            const educationUrl = `${SERVICE_URL}/education`
                + `?name=${encodeURIComponent(visitor.visitorName)}`
                + `&mobile=${encodeURIComponent(visitor.visitorMobile)}`
                + `&areaId=${encodeURIComponent(visitInfo.visitAreaId)}`;
            const message = `[ 아이센스 방문신청 서비스 ]
방문 전 교육 안내

▶ 방문회사 : ${visitInfo.visitCompanyName} ${visitInfo.visitAreaName}
▶ 방문일시 : ${date} ${time}

* 방문 교육을 완료하셔야 접견자의 방문 승인이 진행됩니다.
* 방문 교육을 꼭 이수해 주시기 바랍니다.`;

            const recipientPhone = visitor.visitorMobile;
            await notice.educationSendSms(recipientPhone, message, educationUrl);
        },

        async notificationReEducation(visitInfo, visitor) {
            const date = this.$moment(visitInfo.startDatetime).format('YYYY-MM-DD');
            const time = this.$moment(visitInfo.startDatetime).format('A hh:mm');
            const educationUrl = `${SERVICE_URL}/education`
                + `?name=${encodeURIComponent(visitor.visitorName)}`
                + `&mobile=${encodeURIComponent(visitor.visitorMobile)}`
                + `&areaId=${encodeURIComponent(visitInfo.visitAreaId)}`;
            const message = `[ 아이센스 방문신청 서비스 ]
방문 교육 재이수 안내

▶ 방문회사 : ${visitInfo.visitCompanyName} ${visitInfo.visitAreaName}
▶ 방문일시 : ${date} ${time}

* 방문 신청일 기준 유효기간이 만료되어 방문 교육을 재이수하셔야 합니다.
* 방문 교육을 완료하셔야 접견자의 방문 승인이 진행됩니다.
* 반드시 방문 교육을 이수해 주시기 바랍니다.`;

            const recipientPhone = visitor.visitorMobile;
            await notice.reEducationSendSms(recipientPhone, message, educationUrl);
        },
    }

}